import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  ChevronDown,
  Plus,
  RefreshCw,
  XCircle,
  Pencil,
  Save,
  AlertTriangle,
} from "lucide-react";
import RefreshButton from "../../../Component/Common/RefreshButton";
import RegularLoader from "../../../Component/Loader/RegularLoader";
import { BASE_URL } from "../../../config/api";
import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
import Pagination from "../../../Component/Common/Pagination";
import { useParams } from "react-router-dom";

// ✅ BASE URL
// const BASE_URL = "https://zoho.goelectronix.in/api";

function classNames(...x) {
  return x.filter(Boolean).join(" ");
}

const money = (n) => Number(n || 0).toLocaleString();
const safe = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

// ✅ Handles responses like:
// { data: [...] } OR { data: { data: [...] } } (pagination) OR { brands/categories/models: [...] }
function extractArray(json) {
  if (!json) return [];
  if (Array.isArray(json)) return json;

  const directKeys = [
    "data",
    "brands",
    "categories",
    "models",
    "devices",
    "device_models",
    "items",
    "results",
  ];

  for (const k of directKeys) {
    if (Array.isArray(json?.[k])) return json[k];
  }

  // Laravel pagination: { data: { data: [...] } }
  if (Array.isArray(json?.data?.data)) return json.data.data;

  // Sometimes: { categories: { data: [...] } }
  for (const k of ["brands", "categories", "models", "devices"]) {
    if (Array.isArray(json?.[k]?.data)) return json[k].data;
    if (Array.isArray(json?.[k]?.data?.data)) return json[k].data.data;
  }

  // fallback: first array found
  for (const val of Object.values(json)) {
    if (Array.isArray(val)) return val;
    if (Array.isArray(val?.data)) return val.data;
    if (Array.isArray(val?.data?.data)) return val.data.data;
  }

  return [];
}

function getAuthHeaders() {
  // ✅ Add token if needed
  // const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    // Authorization: Bearer ${token},
  };
}

function LoaderRow({ text = "Loading..." }) {
  return (
    <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
      <div className="flex items-center gap-3">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        <p className="text-sm font-semibold text-blue-700">{text}</p>
      </div>
    </div>
  );
}

function Toast({ type = "success", title, msg, onClose }) {
  if (!title && !msg) return null;
  const styles =
    type === "success"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : "border-rose-200 bg-rose-50 text-rose-800";

  return (
    <div className={classNames("rounded-2xl border p-4", styles)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold">{title}</p>
          {msg ? (
            <p className="mt-1 text-xs font-semibold opacity-90">{msg}</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-xl border border-black/10 bg-white px-2 py-1 text-xs font-semibold hover:bg-white/70"
        >
          Close
        </button>
      </div>
    </div>
  );
}

function Modal({ open, title, onClose, children, max = "max-w-2xl" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div
        className={classNames(
          "relative w-full rounded-3xl border border-blue-100 bg-white shadow-2xl",
          max
        )}
      >
        <div className="flex items-center justify-between gap-3 border-b border-blue-100 p-4">
          <p className="text-base font-semibold text-slate-900">{title}</p>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-2xl border border-blue-100 bg-blue-50 p-2 text-blue-700 hover:bg-blue-100"
          >
            <XCircle className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

export default function BrandModelPage() {

    const { id } = useParams();
  // master data
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);

  // list
  const [models, setModels] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [loadingMaster, setLoadingMaster] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);

  const [variants, setVariants] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);
  // const [variantOpen, setVariantOpen] = useState(false);
  const [editVariantOpen, setEditVariantOpen] = useState(false);
  const [pageSize, setPageSize] = useState(10);


const splitVariant = (name = "") => {
  const [ram, storage] = name.split(" + ");
  return {
    ram: ram || "",
    storage: storage?.replace(" Storage", "") || "",
  };
};

useEffect(() => {
  const closeAll = () => {
    
    setEditVariantOpen(false);
 

  };

  window.addEventListener("click", closeAll);
  return () => window.removeEventListener("click", closeAll);
}, []);

  // search
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [aborter, setAborter] = useState(null);

  // update modal
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    id: null,
    brand_id: "",
    category_id: "",
    name: "",
    storage: "",
    variant_id: "",
    price: "",
  });
  const [updating, setUpdating] = useState(false);

  // toast
  const [toast, setToast] = useState({ type: "success", title: "", msg: "" });
  const closeToast = () => setToast({ type: "success", title: "", msg: "" });

  const brandMap = useMemo(() => {
    const m = new Map();
    brands.forEach((b) => m.set(Number(b.id), b));
    return m;
  }, [brands]);

  const categoryMap = useMemo(() => {
    const m = new Map();
    categories.forEach((c) => m.set(Number(c.id), c));
    return m;
  }, [categories]);

  const selectedBrand = useMemo(() => {
  return brands.find((b) => Number(b.id) === Number(id));
}, [brands, id]);

  const getVariantIdByStorage = (storage) => {
  const v = variants.find(x => x.name === storage);
  return v ? String(v.id) : "";
};


  const fetchBrandsAndCategories = async () => {
    try {
      setLoadingMaster(true);
      const headers = getAuthHeaders();

      const [bRes, cRes] = await Promise.all([
        fetch(`${BASE_URL}warranty/get-brands`, { headers }),
        fetch(`${BASE_URL}warranty/get-categories`, { headers }),
      ]);

      const bJson = await bRes.json();
      const cJson = await cRes.json();

      if (!bRes.ok) throw new Error(bJson?.message || "Failed to load brands");
      if (!cRes.ok)
        throw new Error(cJson?.message || "Failed to load categories");

      setBrands(extractArray(bJson));
      setCategories(extractArray(cJson));
    } catch (e) {
      setToast({
        type: "error",
        title: "Load failed",
        msg: e?.message || "Something went wrong",
      });
    } finally {
      setLoadingMaster(false);
    }
  };

  const fetchModels = async (q = "", page = 1) => {
    try {
      setLoadingModels(true);
      closeToast();

      // cancel previous inflight request
      if (aborter) aborter.abort();
      const controller = new AbortController();
      setAborter(controller);

      const headers = getAuthHeaders();
      const trimmed = String(q || "").trim();

      // ✅ Backend expects ?search=
      const url = trimmed
            ? `${BASE_URL}warranty/device-models?brand_id=${id}&search=${encodeURIComponent(trimmed)}&page=${page}`
            : `${BASE_URL}warranty/device-models?brand_id=${id}&page=${page}`;


      const res = await fetch(url, { headers, signal: controller.signal });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Failed to load device models");

      const pageData = json?.data || {};

      setModels(pageData.data || []);
      setCurrentPage(pageData.current_page || 1);
      setTotalPages(pageData.last_page || 1);
      setAppliedSearch(trimmed);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setToast({
        type: "error",
        title: "Load failed",
        msg: e?.message || "Something went wrong",
      });
    } finally {
      setLoadingModels(false);
    }
  };


  const fetchVariants = async () => {
  try {
    setLoadingVariants(true);

    const res = await fetch(`${BASE_URL}warranty/variants`);
    const json = await res.json();

    if (!res.ok || !json.status) {
      throw new Error("Failed to fetch variants");
    }

    setVariants(json.data || []);
  } catch (err) {
    console.error("Variant fetch error:", err);
    setVariants([]);
  } finally {
    setLoadingVariants(false);
  }
};

  useEffect(() => {
    fetchBrandsAndCategories();
    fetchModels("");
    fetchVariants(); // ✅ ADD THIS
  }, []);


  

  // ✅ debounce search (only if empty or >= 2 chars)
  useEffect(() => {
    const q = search.trim();
    const t = setTimeout(() => {
      if (q.length === 0 || q.length >= 2) fetchModels(q, 1);
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const validate = (f) => {
    if (!f.brand_id) return "Please select Brand";
    if (!f.category_id) return "Please select Category";
    if (!String(f.name || "").trim()) return "Please enter Device Name";
    // if (!String(f.model || "").trim()) return "Please enter Model";
    if (f.price === "" || Number(f.price) <= 0) return "Please enter valid Price";
    if (!f.variant_id) return "Please select Variant";

    return "";
  };


  const openEdit = (m) => {
    closeToast();
    setEditForm({
        id: m.id,
        brand_id: String(m.brand_id ?? ""),
        category_id: String(m.category_id ?? ""),
        name: m.name ?? "",
        storage: m.storage,
        variant_id: getVariantIdByStorage(m.storage),
        price: String(m.price ?? ""),
      });
    setEditOpen(true);
  };

  const onUpdate = async (e) => {
    e.preventDefault();
    closeToast();

    const err = validate(editForm);
    if (err) {
      setToast({ type: "error", title: "Validation", msg: err });
      return;
    }

    if (!editForm.id) {
      setToast({ type: "error", title: "Update failed", msg: "Missing model ID" });
      return;
    }

    try {
      setUpdating(true);
      const headers = getAuthHeaders();

      const brandName =
        brands.find((b) => Number(b.id) === Number(editForm.brand_id))?.name || "";

      const fullName = `${brandName} ${editForm.name}`.trim();
      const variantName = getVariantNameById(editForm.variant_id);

      const payload = {
        brand_id: Number(editForm.brand_id),
        category_id: Number(editForm.category_id),
        name: fullName,
        variant_id: Number(editForm.variant_id), // ✅
        storage: variantName,                   // ✅
        price: Number(editForm.price),
      };


      const res = await fetch(`${BASE_URL}warranty/device-models/${editForm.id}`, {
        method: "PUT", // change to PATCH if needed
        headers,
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Failed to update model");

      setToast({
        type: "success",
        title: "Updated",
        msg: `Updated: ${payload.name} (${payload.model})`,
      });

      setEditOpen(false);
      await fetchModels(search.trim());
    } catch (e2) {
      setToast({
        type: "error",
        title: "Update failed",
        msg: e2?.message || "Something went wrong",
      });
    } finally {
      setUpdating(false);
    }
  };

  const getVariantNameById = (id) =>
  variants.find((v) => Number(v.id) === Number(id))?.name || "";


  return (
    <div className="space-y-4">
      <Toast type={toast.type} title={toast.title} msg={toast.msg} onClose={closeToast} />

      {selectedBrand && (
        <div className="relative overflow-hidden rounded-3xl bg-white shadow-lg border">

            {/* Gradient Header Section */}
            <div className="bg-gradient-to-r from-indigo-500 via-blue-400 to-purple-400 px-8 pt-10 pb-10">

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">

                {/* LEFT SIDE */}
                <div className="flex items-center gap-6">

                {/* Logo */}
                <div className="h-24 w-24 rounded-3xl bg-white shadow-xl border-4 border-white overflow-hidden flex items-center justify-center">
                    {selectedBrand.image ? (
                    <img
                        src={selectedBrand.image}
                        alt={selectedBrand.name}
                        className="h-full w-full object-cover"
                    />
                    ) : (
                    <span className="text-4xl font-bold text-indigo-600">
                        {selectedBrand.name?.charAt(0)}
                    </span>
                    )}
                </div>

                {/* Brand Info */}
                <div>
                    <h2 className="text-3xl font-bold text-white">
                    {selectedBrand.name}
                    </h2>

                    {/* Categories */}
                    <div className="flex flex-wrap gap-2 mt-3">
                    {selectedBrand.categories?.map((cat) => (
                        <span
                        key={cat.id}
                        className="px-3 py-1 text-xs rounded-full bg-white/20 text-white backdrop-blur-sm"
                        >
                        {cat.name}
                        </span>
                    ))}
                    </div>
                </div>
                </div>

                {/* RIGHT SIDE STATS */}
                <div className="grid grid-cols-2 gap-10 text-center text-white">

                <div>
                    <p className="text-xs opacity-80">Models</p>
                    <p className="text-2xl font-bold">
                    {models.length}
                    </p>
                </div>

                {/* <div>
                    <p className="text-xs opacity-80">Categories</p>
                    <p className="text-2xl font-bold">
                    {selectedBrand.categories?.length || 0}
                    </p>
                </div> */}

                <div>
                    <p className="text-xs opacity-80">Status</p>
                    <span
                    className={`px-3 py-1 text-xs rounded-full font-semibold ${
                        selectedBrand.status === 1
                        ? "bg-emerald-200 text-emerald-800"
                        : "bg-rose-200 text-rose-800"
                    }`}
                    >
                    {selectedBrand.status === 1 ? "Active" : "Inactive"}
                    </span>
                </div>

                </div>

            </div>
            </div>

            {/* White Content Section */}
            <div className="px-8 pb-8">

            {/* Description */}
            {selectedBrand.description && (
                <div className="mt-8">
                <p className="text-sm font-semibold text-slate-700 mb-2">
                    About Brand
                </p>
                <p className="text-sm text-slate-600 leading-relaxed max-w-3xl">
                    {selectedBrand.description}
                </p>
                </div>
            )}

            {/* Dates */}
            <div className="mt-6 flex flex-wrap gap-6 text-sm text-slate-500">
                <span>
                Created:{" "}
                <span className="font-semibold text-slate-700">
                    {new Date(selectedBrand.created_at).toLocaleDateString()}
                </span>
                </span>

                <span>
                Updated:{" "}
                <span className="font-semibold text-slate-700">
                    {new Date(selectedBrand.updated_at).toLocaleDateString()}
                </span>
                </span>
            </div>

            </div>
        </div>
        )}





      {/* List + Search */}
      <div className="rounded-3xl border border-blue-100 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
           <p className="text-base font-semibold text-slate-900">
            Brand Models
            </p>
            <p className="mt-1 text-xs font-semibold text-slate-500">
              {appliedSearch ? (
                <>
                  Showing results for{" "}
                  <span className="font-semibold text-slate-900">“{appliedSearch}”</span>
                </>
              ) : (
                "Showing all results"
              )}
            </p>
          </div>

          <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center">
            <div className="w-full md:w-96">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      fetchModels(search.trim());
                    }
                  }}
                  placeholder="Search by name / model / price..."
                  className="h-11 w-full rounded-2xl border border-blue-100 bg-white py-2 pl-10 pr-3 text-sm font-semibold text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              
            </div>

            <button
              type="button"
              onClick={() => fetchModels(search.trim())}
              className="inline-flex h-11 items-center justify-center rounded-2xl border border-blue-200 bg-blue-50 px-4 text-sm font-semibold text-blue-700 hover:bg-blue-100"
            >
              Search
            </button>

            <button
              type="button"
              onClick={() => {
                setSearch("");
                fetchModels("");
              }}
              className="inline-flex h-11 items-center justify-center rounded-2xl border border-blue-200 bg-white px-4 text-sm font-semibold text-blue-700 hover:bg-blue-50"
            >
              Reset
            </button>

            <RefreshButton onClick={fetchModels}/>
          </div>
        </div>

        {loadingModels ? <RegularLoader/> : <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b text-left text-slate-700">
                <th className="py-3">Device</th>
                <th>Brand</th>
                <th>Category</th>
                <th>Storage</th>
                <th>Price</th>
                <th className="text-right">Action</th>
              </tr>
            </thead>

            <tbody>
              {!loadingModels && models.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="py-10 text-center text-sm font-semibold text-slate-500"
                  >
                    No models found.
                  </td>
                </tr>
              ) : null}

              {models.map((m) => {
                const brand = brandMap.get(Number(m.brand_id));
                const category = categoryMap.get(Number(m.category_id));
                const modelVal = m.model ?? m.storage ?? "";

                return (
                  <tr key={m.id || `${m.name}-${modelVal}`} className="border-b hover:bg-slate-50">
                    <td className="py-3">
                      <p className="text-sm font-semibold text-slate-900">{safe(m.name)}</p>
                      <p className="mt-0.5 text-xs font-semibold text-slate-500">
                        ID: <span className="font-semibold">{safe(m.id)}</span>
                      </p>
                    </td>
                    <td className="font-semibold text-slate-700">{brand?.name || safe(m.brand_id)}</td>
                    <td className="font-semibold text-slate-700">{category?.name || safe(m.category_id)}</td>
                    <td className="font-semibold text-slate-700">{safe(modelVal)}</td>
                    <td className="font-semibold text-slate-900">₹ {money(m.price)}</td>
                    <td className="text-right">
                      <button
                        type="button"
                        onClick={() => openEdit(m)}
                        className="inline-flex items-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                      >
                        <Pencil className="h-4 w-4" />
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>}

        {!loadingModels && totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => fetchModels(appliedSearch, page)}
          />
        )}

      </div>

      {/* Update Modal */}
      <Modal
        open={editOpen}
        title={`Update Model (ID: ${editForm.id ?? "—"})`}
        onClose={() => setEditOpen(false)}
        max="max-w-3xl"
      >
        <div className="mb-3 rounded-3xl border border-blue-100 bg-blue-50 p-4">
          <div className="flex items-start gap-3">
            <div className="rounded-2xl bg-white p-3 text-blue-700">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">Update uses</p>
              {/* <p className="mt-1 text-xs font-semibold text-slate-600">
                <span className="font-semibold">PUT</span> {BASE_URL}/warranty/device-models/
                <span className="font-semibold">{editForm.id}</span>
              </p> */}
            </div>
          </div>
        </div>

        <form onSubmit={onUpdate} className="grid grid-cols-1 gap-3 md:grid-cols-12">
          <div className="md:col-span-3">
            <label className="mb-1 block text-xs font-semibold text-slate-600">Brand</label>
            <div className="relative">
              <select
                value={editForm.brand_id}
                onChange={(e) => setEditForm((s) => ({ ...s, brand_id: e.target.value }))}
                className="h-11 w-full appearance-none rounded-2xl border border-blue-100 bg-white px-3 text-sm font-semibold text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="">Select Brand</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name || `Brand #${b.id}`}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>
          </div>

          <div className="md:col-span-3">
            <label className="mb-1 block text-xs font-semibold text-slate-600">Category</label>
            <div className="relative">
              <select
                value={editForm.category_id}
                onChange={(e) => setEditForm((s) => ({ ...s, category_id: e.target.value }))}
                className="h-11 w-full appearance-none rounded-2xl border border-blue-100 bg-white px-3 text-sm font-semibold text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="">Select Category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || `Category #${c.id}`}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>
          </div>

          <div className="md:col-span-4">
            <label className="mb-1 block text-xs font-semibold text-slate-600">Model</label>
            <input
              value={editForm.name}
              onChange={(e) => setEditForm((s) => ({ ...s, name: e.target.value }))}
              className="h-11 w-full rounded-2xl border border-blue-100 bg-white px-3 text-sm font-semibold text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>

          {/* Variant (Update) */}
          <div className="md:col-span-3">
            <label className="mb-1 block text-xs font-semibold text-slate-600">
              Variant
            </label>

            <div
              className="relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setEditVariantOpen(o => !o)}
                className={`
                  h-11 w-full rounded-2xl border px-4 pr-10
                  flex items-center justify-between
                  text-sm font-semibold bg-white
                  ${editForm.variant_id
                    ? "border-blue-500 text-slate-900"
                    : "border-blue-300 text-slate-400"}
                `}
              >
                {editForm.variant_id
                  ? (() => {
                      const v = variants.find(
                        x => Number(x.id) === Number(editForm.variant_id)
                      );
                      const { ram, storage } = splitVariant(v?.name);
                      return `${ram} + ${storage}`;
                    })()
                  : "Select Variant"}

                <ChevronDown className="h-4 w-4 text-slate-500" />
              </button>

              {editVariantOpen && (
                <div className="
                  absolute z-50 mt-2 w-full max-h-64 overflow-y-auto
                  rounded-2xl border border-blue-200 bg-white shadow-xl
                ">
                  {variants.map(v => {
                    const { ram, storage } = splitVariant(v.name);
                    const active = Number(editForm.variant_id) === Number(v.id);

                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => {
                          setEditForm(s => ({
                            ...s,
                            variant_id: v.id,
                            storage: v.name, // optional but useful
                          }));
                          setEditVariantOpen(false);
                        }}
                        className={`
                          w-full px-4 py-3 flex justify-between text-sm
                          ${active
                            ? "bg-blue-50 text-blue-700"
                            : "text-slate-800 hover:bg-slate-50"}
                        `}
                      >
                        <span className="font-semibold">{ram}</span>
                        <span className="text-xs font-semibold text-slate-500">
                          {storage}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>


          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-semibold text-slate-600">Price</label>
            <input
              type="number"
              value={editForm.price}
              onChange={(e) => setEditForm((s) => ({ ...s, price: e.target.value }))}
              className="h-11 w-full rounded-2xl border border-blue-100 bg-white px-3 text-sm font-semibold text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>

          <div className="md:col-span-12 flex flex-col gap-2 md:flex-row md:justify-end">
            <button
              type="button"
              onClick={() => setEditOpen(false)}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border border-blue-200 bg-white px-4 text-sm font-semibold text-blue-700 hover:bg-blue-50"
              disabled={updating}
            >
              <XCircle className="h-4 w-4" />
              Cancel
            </button>

            <button
              type="submit"
              disabled={updating}
              className={classNames(
                "inline-flex h-11 items-center justify-center gap-2 rounded-2xl px-5 text-sm font-semibold text-white",
                updating ? "bg-blue-400" : "bg-blue-600 hover:bg-blue-700"
              )}
            >
              {updating ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/90 border-t-transparent" />
                  Updating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Update
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}